import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, Menu, X, MessageSquare, User, ArrowRight, Lock } from 'lucide-react';
import { useAuth } from '../src/contexts/AuthContext';

interface NavbarProps {
  darkMode: boolean;
  toggleTheme: () => void;
  onShowSafeIntro?: () => void;
}

const navItems = [
  { name: 'Who am I?', id: 'about', short: 'About' },
  { name: 'What am I capable of?', id: 'capabilities', short: 'Skills' },
  { name: 'My working model', id: 'process', short: 'Model' },
  { name: 'How to contact me?', id: 'contact', short: 'Contact' },
];

export const Navbar: React.FC<NavbarProps> = ({ darkMode, toggleTheme, onShowSafeIntro }) => {
  const [activeSection, setActiveSection] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, profile, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  const isAdmin = profile?.role === 'admin' || (user?.email && isPlatformAdmin(user.email));

  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + window.innerHeight / 2.5;

      // Find the section that is currently in view
      let currentSection = '';
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= window.innerHeight / 2.5 && rect.bottom >= window.innerHeight / 2.5) {
            currentSection = navItems[i].id;
            break;
          }
        }
      }
      
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  const scrollToSection = (id: string) => {
    setIsMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <motion.nav 
        className="fixed top-4 sm:top-6 md:top-7 left-0 right-0 z-[100] flex justify-center items-center px-2 sm:px-4 md:px-6 pointer-events-none"
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        ref={menuRef}
      >
        {/* Main Header Assembly Container */}
        <div className="relative pointer-events-auto w-full max-w-[96%] sm:max-w-xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl flex flex-col items-center">
          
          {/* Brown Leather Watch Strap Bar */}
          <div 
            className="w-full relative flex items-center justify-between h-14 sm:h-16 md:h-16 lg:h-20 rounded-full border border-[#2e1509] shadow-[0_12px_32px_rgba(0,0,0,0.9),inset_0_2px_3px_rgba(255,255,255,0.22),inset_0_-2px_5px_rgba(0,0,0,0.95)] backdrop-blur-md px-3 sm:px-4 md:px-5 lg:px-6"
            style={{
              background: 'linear-gradient(180deg, #4d2714 0%, #381b0e 38%, #241108 72%, #140803 100%)',
            }}
          >
            {/* Genuine Saddle Stitching along Strap Perimeter */}
            <div className="absolute inset-[3px] sm:inset-[4px] lg:inset-[4.5px] rounded-full border border-dashed border-[#d8aa68]/45 pointer-events-none z-10 shadow-[inset_0_0_6px_rgba(0,0,0,0.6)]" />

            {/* LEFT WING (w-1/2): 'Let's Talk' Button + Desktop Nav Links */}
            <div className="w-1/2 flex items-center justify-start gap-2 sm:gap-3 pl-2 sm:pl-3 pr-8 sm:pr-10 md:pr-14 lg:pr-18 xl:pr-20 z-20 overflow-visible">
              {/* LET'S TALK CTA Button placed BEFORE 'Who am I?' (Visible on both Mobile and Desktop) */}
              <button 
                id="nav-cta-talk"
                onClick={() => scrollToSection('contact')}
                className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 bg-gradient-to-b from-[#e5bb73] via-[#cfa358] to-[#996f2a] text-[#1a0c04] font-mono font-bold text-[10px] sm:text-xs xl:text-sm rounded-full border border-[#fff0c8] shadow-[0_2px_8px_rgba(0,0,0,0.65),inset_0_1px_1.5px_rgba(255,255,255,0.7)] hover:brightness-110 hover:shadow-[0_0_14px_rgba(229,187,115,0.5)] active:scale-95 transition-all duration-200 whitespace-nowrap shrink-0 cursor-pointer"
                title="Let's Talk"
              >
                <MessageSquare size={12} className="text-[#1a0c04] stroke-[2.5] sm:w-[13px] sm:h-[13px]" />
                <span>Let's Talk</span>
              </button>

              {/* Left Navigation Buttons (Desktop Only) */}
              <div className="hidden lg:flex items-center gap-2 xl:gap-3 shrink-0">
                {/* WHO AM I? Button */}
                <button
                  id="nav-btn-who-am-i"
                  onClick={() => scrollToSection('about')}
                  className={`relative px-3 py-1 text-xs xl:text-sm font-mono uppercase tracking-wider transition-all duration-200 rounded-full whitespace-nowrap shrink-0 ${
                    activeSection === 'about'
                      ? 'text-[#ffe0a3] font-bold bg-[#1b0d06]/90 border border-[#d8aa68]/70 shadow-[inset_0_1px_2px_rgba(0,0,0,0.7)]'
                      : 'text-[#f5ebe0]/90 hover:text-[#ffe0a3] hover:bg-[#1b0d06]/40'
                  }`}
                >
                  <span>Who am I?</span>
                </button>

                {/* WHAT AM I CAPABLE OF? Button */}
                <button
                  id="nav-btn-what-am-i-capable-of"
                  onClick={() => scrollToSection('capabilities')}
                  className={`relative px-3 py-1 text-xs xl:text-sm font-mono uppercase tracking-wider transition-all duration-200 rounded-full whitespace-nowrap shrink-0 ${
                    activeSection === 'capabilities'
                      ? 'text-[#ffe0a3] font-bold bg-[#1b0d06]/90 border border-[#d8aa68]/70 shadow-[inset_0_1px_2px_rgba(0,0,0,0.7)]'
                      : 'text-[#f5ebe0]/90 hover:text-[#ffe0a3] hover:bg-[#1b0d06]/40'
                  }`}
                >
                  <span>What am I capable of?</span>
                </button>
              </div>
            </div>

            {/* RIGHT WING (w-1/2): Right Nav Links + Mobile Menu Button + Right Rivet */}
            <div className="w-1/2 flex items-center justify-end gap-1.5 sm:gap-2.5 pl-8 sm:pl-10 md:pl-14 lg:pl-18 xl:pl-20 pr-1 sm:pr-2 z-20 overflow-visible">
              {/* Right Navigation & Action Items (Desktop Only) */}
              <div className="hidden lg:flex items-center gap-2 xl:gap-3 shrink-0">
                {/* MY WORKING MODEL Button (Desktop Only) */}
                <button
                  id="nav-btn-my-working-model"
                  onClick={() => scrollToSection('process')}
                  className={`relative px-3 py-1 text-xs xl:text-sm font-mono uppercase tracking-wider transition-all duration-200 rounded-full whitespace-nowrap shrink-0 ${
                    activeSection === 'process'
                      ? 'text-[#ffe0a3] font-bold bg-[#1b0d06]/90 border border-[#d8aa68]/70 shadow-[inset_0_1px_2px_rgba(0,0,0,0.7)]'
                      : 'text-[#f5ebe0]/90 hover:text-[#ffe0a3] hover:bg-[#1b0d06]/40'
                  }`}
                >
                  <span>My working model</span>
                </button>

                {/* HOW TO CONTACT ME? Button (Desktop Only) */}
                <button
                  id="nav-btn-how-to-contact-me"
                  onClick={() => scrollToSection('contact')}
                  className={`relative px-3 py-1 text-xs xl:text-sm font-mono uppercase tracking-wider transition-all duration-200 rounded-full whitespace-nowrap shrink-0 ${
                    activeSection === 'contact'
                      ? 'text-[#ffe0a3] font-bold bg-[#1b0d06]/90 border border-[#d8aa68]/70 shadow-[inset_0_1px_2px_rgba(0,0,0,0.7)]'
                      : 'text-[#f5ebe0]/90 hover:text-[#ffe0a3] hover:bg-[#1b0d06]/40'
                  }`}
                >
                  <span>How to contact me?</span>
                </button>
              </div>

              {/* Mobile / Tablet Dropdown Menu Trigger Button (RIGHT side of the logo on mobile) */}
              <button
                id="nav-mobile-menu-toggle"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 text-[#ffe0a3] bg-gradient-to-b from-[#381b0e] to-[#1a0c04] border border-[#d8aa68] rounded-full transition-all hover:brightness-125 shadow-[0_2px_8px_rgba(0,0,0,0.7),inset_0_1px_1px_rgba(255,255,255,0.15)] shrink-0 cursor-pointer active:scale-95"
                aria-label="Toggle Navigation Menu"
              >
                {isMobileMenuOpen ? <X size={13} className="text-[#ffd885]" /> : <Menu size={13} className="text-[#ffd885]" />}
                <span className="text-[10px] sm:text-xs font-mono font-bold uppercase tracking-wider text-[#ffe0a3]">Menu</span>
              </button>

              {/* Right End Brass Bolt Pin / Rivet */}
              <div className="flex items-center shrink-0">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-gradient-to-br from-[#ffe5a3] via-[#b88942] to-[#593d18] shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_1.5px_3px_rgba(0,0,0,0.85)] border border-[#3d2209] flex items-center justify-center">
                  <div className="w-0.5 h-0.5 rounded-full bg-[#1b0a02]" />
                </div>
              </div>
            </div>

            {/* PROMINENT HIGH-VISIBILITY CENTER PELICAN COIN MEDALLION (Dead Center) */}
            <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 z-30 pointer-events-auto flex items-center justify-center">
              {/* Circular Brown Leather Watch Bezel Pad */}
              <div 
                className="relative w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 aspect-square rounded-full p-1 sm:p-1.5 flex items-center justify-center border-2 sm:border-2.5 border-[#caa062] shadow-[0_12px_28px_rgba(0,0,0,0.95),0_0_18px_rgba(216,170,104,0.4)] transition-all duration-300 shrink-0"
                style={{
                  background: '#381b0e',
                }}
              >
                {/* Clickable Pelican Coin Medallion */}
                <button 
                  id="nav-logo-button"
                  onClick={() => scrollToSection('home')}
                  className="group relative rounded-full transition-transform duration-300 hover:scale-105 active:scale-95 focus:outline-none flex items-center justify-center p-0.5 w-full h-full aspect-square overflow-hidden shrink-0"
                  title="The Brown Pelican Man · Dive Deep, Build Big"
                >
                  {/* Subtle Warm Amber Glow on Hover */}
                  <div className="absolute inset-0 rounded-full bg-[#d8aa68]/20 blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  
                  {/* Well-proportioned Medallion Emblem with full visibility without any edge clipping */}
                  <img 
                    src="/brand_logo.png?v=4" 
                    alt="The Brown Pelican Man Logo" 
                    referrerPolicy="no-referrer"
                    className="w-full h-full aspect-square rounded-full object-contain transition-transform duration-300 shrink-0 select-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" 
                  />
                </button>
              </div>
            </div>
          </div>

          {/* RESPONSIVE DROPDOWN DRAWER (For Mobile / Compact Screens) */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="w-full mt-2 rounded-2xl border border-[#d8aa68]/40 shadow-[0_16px_36px_rgba(0,0,0,0.95),inset_0_1px_1.5px_rgba(255,255,255,0.15)] backdrop-blur-xl p-4 lg:hidden"
                style={{
                  background: 'linear-gradient(180deg, #381b0e 0%, #241108 50%, #140803 100%)',
                }}
              >
                <div className="flex flex-col gap-2">
                  {/* Dropdown Brand Header */}
                  <div className="flex items-center gap-2.5 px-2 py-2 border-b border-[#d8aa68]/20 mb-1">
                    <img 
                      src="/brand_logo.png?v=4" 
                      alt="Brand Logo" 
                      referrerPolicy="no-referrer"
                      className="w-7 h-7 object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)] shrink-0" 
                    />
                    <span className="font-mono font-black text-xs tracking-wider uppercase bg-gradient-to-r from-[#ffe0a3] via-[#f7cb84] to-[#caa062] bg-clip-text text-transparent">
                      The Brown Pelican Man
                    </span>
                  </div>

                  {navItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-mono uppercase tracking-wider transition-all ${
                        activeSection === item.id
                          ? 'bg-[#1b0d06] text-[#ffe0a3] font-bold border border-[#d8aa68]/60 shadow-[inset_0_1px_2px_rgba(0,0,0,0.6)]'
                          : 'text-[#f5ebe0]/90 hover:bg-[#1b0d06]/50 hover:text-[#ffe0a3]'
                      }`}
                    >
                      <span>{item.name}</span>
                      <ArrowRight size={13} className="text-[#d8aa68]/70" />
                    </button>
                  ))}

                  <div className="h-px w-full bg-[#d8aa68]/20 my-1" />

                  {/* Let's Talk CTA in Dropdown (Consistent with Header Button) */}
                  <button
                    onClick={() => scrollToSection('contact')}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-b from-[#e5bb73] via-[#cfa358] to-[#996f2a] text-[#1a0c04] border border-[#fff0c8] text-xs font-mono font-bold uppercase tracking-widest shadow-[0_2px_10px_rgba(207,163,88,0.3)] hover:brightness-110 transition-all"
                  >
                    <MessageSquare size={14} className="text-[#1a0c04] stroke-[2.5]" />
                    <span>Let's Talk</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </motion.nav>
    </>
  );
};
